import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'

import { setDelayToSave } from '../../Slices/Kiosk/ArticleDetailSlice'
import { quantityLimitDefaultInputChannelName } from '../../Constants/TableConfiguration'
import InputNumberDiwa from '../Generic/InputNumberDiwa'

function LimitDelayInput(props) {
  const dispatch = useAppDispatch()
  const quantityLimitData = useAppSelector(state => state.present.articleDetail.articleSelected.quantityLimitData)

  const [inputActive, setInputActive] = useState(false)

  const channelValue = useMemo(() => {
    return props.isDefault ? quantityLimitDefaultInputChannelName : props.channelName
  }, [props.channelName, props.isDefault])

  const maxDelay = useMemo(() => {
    return quantityLimitData.defaultDelay && quantityLimitData.defaultDelay !== -1
      ? quantityLimitData.defaultDelay
      : quantityLimitData.defaultDelay === 0
      ? quantityLimitData.defaultDelay
      : 0
  }, [quantityLimitData.defaultDelay])

  const originalValue = useMemo(() => {
    const channelStored =
      quantityLimitData.channelDelays && quantityLimitData.channelDelays.length > 0
        ? quantityLimitData.channelDelays
        : []
    const valueToReturn = channelStored.find(x => x.channelName === channelValue)

    return valueToReturn
  }, [channelValue, quantityLimitData.channelDelays])

  const valueToShow = useMemo(() => {
    if (props.isDefault) {
      return maxDelay
    }

    return originalValue && originalValue.delay !== -1
      ? originalValue.delay
      : originalValue.delay === 0
      ? originalValue.delay
      : originalValue.delay === -1
      ? maxDelay
      : null
  }, [maxDelay, originalValue, props.isDefault])

  const isUnique = useMemo(() => {
    return valueToShow && valueToShow !== maxDelay
  }, [maxDelay, valueToShow])

  const isDelayExpired = useMemo(() => {
    const delaySinceAvailable = quantityLimitData.daysSinceAvailable
    const valueToCompare = valueToShow
    return delaySinceAvailable >= valueToCompare
  }, [quantityLimitData.daysSinceAvailable, valueToShow])

  const isChanged = useMemo(() => {
    const checkResult = props.delayChanged.find(x => x === channelValue)
    return checkResult
  }, [channelValue, props.delayChanged])

  const isDefaultChanged = useMemo(() => {
    return props.delayChanged.find(x => x === quantityLimitDefaultInputChannelName)
  }, [props.delayChanged])

  const storeDelay = useCallback(
    value => {
      setInputActive(false)

      if (isDelayExpired && !isChanged) {
        return
      }
      const valueRounded = value || value === 0 ? Math.round(value) : props.isDefault ? 0 : -1

      if (valueRounded !== valueToShow) {
        const delayObj = {
          delay: valueRounded,
          channelName: channelValue,
        }

        props.setChangedDelay(oldArray => {
          if (props.isDefault) {
            const channelStored =
              quantityLimitData.channelDelays && quantityLimitData.channelDelays.length > 0
                ? quantityLimitData.channelDelays
                : []
            const mappedChannels = channelStored
              .map(x => {
                return x && x.channelName && x.delay === -1 ? x.channelName : null
              })
              .filter(x => x)

            const filteredValues = oldArray.filter(x => x !== channelValue && mappedChannels.indexOf(x) === -1)

            const valuesToAdd = [...mappedChannels, channelValue]

            return [...filteredValues, ...valuesToAdd]
          }

          const filteredWithoutDefault = oldArray.filter(x => x !== channelValue)
          return [...filteredWithoutDefault, channelValue]
        })

        dispatch(setDelayToSave(delayObj))
      }
    },
    [isDelayExpired, isChanged, props, channelValue, dispatch, quantityLimitData.channelDelays, valueToShow],
  )

  const setEditInput = useCallback(() => {
    if (isDelayExpired && !isChanged) {
      return
    }

    setInputActive(true)
  }, [isChanged, isDelayExpired])

  const onInputPress = useCallback(event => {
    if (event.key === 'Escape') {
      setInputActive(false)
    }
  }, [])

  return (
    <div>
      <InputNumberDiwa
        className={
          !inputActive
            ? valueToShow || valueToShow === 0
              ? 'count-box delay secondcolor' +
                (isUnique ? ' bold' : '') +
                (isChanged || (isDefaultChanged && originalValue.delay === -1) ? ' changed' : '') +
                (isDelayExpired ? ' expired noedit ' : '')
              : 'count-box delay empty-content'
            : 'num-input quantity-limit-input'
        }
        min={0}
        max={999}
        controls={false}
        value={valueToShow}
        onFocus={!inputActive ? setEditInput : undefined}
        onValueConfirm={inputActive ? storeDelay : undefined}
        onKeyDown={inputActive ? onInputPress : undefined}
      />
    </div>
  )
}

export default LimitDelayInput
